// MP4Box.js → WebCodecs Demux/Decode Starter (Audio+Video)
// Integrates with AudioPipeline (main thread) and a Video presenter (optional)

const debugEnabled = () => (typeof window !== 'undefined' && window.vybyDebugWCOverlay === true)

function cloneToBuffer(bytes) {
  if (!bytes) return null
  if (bytes instanceof ArrayBuffer) return bytes.slice(0)
  if (bytes.buffer instanceof ArrayBuffer) {
    const offset = bytes.byteOffset || 0
    const length = bytes.byteLength || bytes.length || 0
    return bytes.buffer.slice(offset, offset + length)
  }
  const view = new Uint8Array(bytes)
  return view.buffer.slice(0)
}

function readEsdsLength(view, start) {
  let pos = start
  let size = 0
  for (let i = 0; i < 4; i++) {
    if (pos >= view.length) return { size: 0, next: view.length }
    const byte = view[pos++]
    size = (size << 7) | (byte & 0x7f)
    if ((byte & 0x80) === 0) break
  }
  return { size, next: pos }
}

function extractDecoderSpecificInfo(esdsBuf) {
  if (!esdsBuf) return null
  const view = new Uint8Array(esdsBuf)
  let pos = 0

  const seek = (limit) => {
    while (pos < limit && pos < view.length) {
      const tag = view[pos++]
      const { size, next } = readEsdsLength(view, pos)
      pos = next
      const payloadEnd = Math.min(pos + size, view.length)
      if (tag === 0x05) {
        return view.slice(pos, payloadEnd).buffer.slice(0)
      }
      if (tag === 0x03) {
        if (pos + 2 <= payloadEnd) pos += 2 // ES_ID
        if (pos < payloadEnd) {
          const flags = view[pos++] || 0
          if (flags & 0x80) pos += 2
          if (flags & 0x40 && pos < payloadEnd) {
            const urlLen = view[pos++] || 0
            pos += urlLen
          }
          if (flags & 0x20) pos += 2
        }
        const nested = seek(payloadEnd)
        if (nested) return nested
        pos = payloadEnd
        continue
      }
      if (tag === 0x04) {
        pos += 13
        const nested = seek(payloadEnd)
        if (nested) return nested
        pos = payloadEnd
        continue
      }
      const nested = seek(payloadEnd)
      if (nested) return nested
      pos = payloadEnd
    }
    return null
  }

  return seek(view.length)
}

function extractEntryDescription(entry, codecLabel, tag) {
  if (!entry) return null

  const toBuffer = (source, infoTag) => {
    if (!source) return null

    if (typeof source.toUint8Array === 'function') {
      const raw = source.toUint8Array()
      const buf = cloneToBuffer(raw)
      if (buf && debugEnabled()) console.info(`[MP4WC] ${tag} ${infoTag} (toUint8Array)`, codecLabel, buf.byteLength)
      return buf
    }

    const direct = toUint8ArrayMaybe(source.data)
      || toUint8ArrayMaybe(source.raw_data)
      || toUint8ArrayMaybe(source.bytes)
      || toUint8ArrayMaybe(source)

    if (direct) {
      const buf = cloneToBuffer(direct)
      if (buf && debugEnabled()) console.info(`[MP4WC] ${tag} ${infoTag} (direct bytes)`, codecLabel, buf.byteLength)
      return buf
    }

    if (debugEnabled()) console.info(`[MP4WC] ${tag} ${infoTag} missing bytes`, codecLabel, { keys: Object.keys(source || {}) })
    return null
  }

  const avcC = entry.avcC || entry.AVCDecoderConfigurationRecord || null
  if (avcC) {
    const buf = toBuffer(avcC, 'avcC config bytes')
    if (buf) return buf
    const rebuilt = buildAvcDescription(avcC, codecLabel, tag)
    if (rebuilt) return rebuilt
  }

  const hvcC = entry.hvcC || entry.HEVCDecoderConfigurationRecord || null
  if (hvcC) {
    const buf = toBuffer(hvcC, 'hvcC config bytes')
    if (buf) return buf
  }

  const av1C = entry.av1C || null
  if (av1C) {
    const buf = toBuffer(av1C, 'av1C config bytes')
    if (buf) return buf
  }

  const vpcC = entry.vpcC || null
  if (vpcC) {
    const buf = toBuffer(vpcC, 'vpcC config bytes')
    if (buf) return buf
  }

  const esds = entry.esds || entry.ESD || null
  if (esds) {
    const dsi = esds.decoderSpecificInfo || esds.decConfigDescr?.decoderSpecificInfo || null
    const bytes = dsi?.toUint8Array ? dsi.toUint8Array() : (dsi?.data || esds?.toUint8Array?.() || esds?.data || null)
    let buf = cloneToBuffer(bytes)
    if (buf) {
      if (debugEnabled()) console.info(`[MP4WC] ${tag} esds bytes`, codecLabel, new Uint8Array(buf).byteLength)
      const trimmed = extractDecoderSpecificInfo(buf)
      if (trimmed && trimmed.byteLength > 0 && trimmed.byteLength < new Uint8Array(buf).byteLength) {
        if (debugEnabled()) console.info(`[MP4WC] ${tag} decoderSpecificInfo`, codecLabel, trimmed.byteLength)
        return trimmed
      }
      return buf
    }
  }

  return null
}

function toUint8ArrayMaybe(value) {
  if (!value) return null
  if (value instanceof Uint8Array) return value
  if (value instanceof ArrayBuffer) return new Uint8Array(value)
  if (Array.isArray(value)) return new Uint8Array(value)
  return null
}

function buildAvcDescription(record, codecLabel, tag) {
  const source = record?.config || record
  if (!source) return null

  let spsList = Array.isArray(source.SPS)
    ? source.SPS
    : Array.isArray(source.sps)
      ? source.sps
      : source.sps_units ?? source.spsUnits ?? (Array.isArray(source['SPS ']) ? source['SPS '] : null)
  let ppsList = Array.isArray(source.PPS)
    ? source.PPS
    : Array.isArray(source.pps)
      ? source.pps
      : source.pps_units ?? source.ppsUnits ?? (Array.isArray(source['PPS ']) ? source['PPS '] : null)

  // Some MP4Box builds expose SPS/PPS inside the first sample description
  if ((!spsList || !ppsList) && Array.isArray(record?.samples)) {
    const first = record.samples.find((s) => Array.isArray(s?.sps) || Array.isArray(s?.pps))
    if (first) {
      const sampleSps = Array.isArray(first.sps) ? first.sps : Array.isArray(first?.description?.sps) ? first.description.sps : null
      const samplePps = Array.isArray(first.pps) ? first.pps : Array.isArray(first?.description?.pps) ? first.description.pps : null
      if (!spsList && sampleSps) {
    if (debugEnabled()) console.info('[MP4WC] using SPS from sample description')
        spsList = sampleSps
      }
      if (!ppsList && samplePps) {
    if (debugEnabled()) console.info('[MP4WC] using PPS from sample description')
        ppsList = samplePps
      }
    }
  }

  const spsBuffers = (spsList || [])
    .map((item) => toUint8ArrayMaybe(item?.nal_unit || item?.nalu || item?.data || item?.bytes || item))
    .filter(Boolean)
  const ppsBuffers = (ppsList || [])
    .map((item) => toUint8ArrayMaybe(item?.nal_unit || item?.nalu || item?.data || item?.bytes || item))
    .filter(Boolean)

  if (!spsBuffers.length || !ppsBuffers.length) {
    if (debugEnabled()) console.info(`[MP4WC] ${tag} avcC missing SPS/PPS arrays`, codecLabel)
    return null
  }

  const configurationVersion = source.configurationVersion ?? 1
  const profile = source.AVCProfileIndication ?? source.profile_idc ?? record.AVCProfileIndication ?? record.profile_idc ?? 0
  const compatibility = source.profile_compatibility ?? record.profile_compatibility ?? 0
  const level = source.AVCLevelIndication ?? source.level_idc ?? record.AVCLevelIndication ?? record.level_idc ?? 0
  const lengthSizeMinusOne = (source.lengthSizeMinusOne ?? (source.nalu_length_size ? source.nalu_length_size - 1 : 3)) & 0x03

  let total = 7
  spsBuffers.forEach((buf) => { total += 2 + buf.byteLength })
  total += 1
  ppsBuffers.forEach((buf) => { total += 2 + buf.byteLength })

  const out = new Uint8Array(total)
  let offset = 0
  out[offset++] = configurationVersion
  out[offset++] = profile
  out[offset++] = compatibility
  out[offset++] = level
  out[offset++] = 0xfc | lengthSizeMinusOne
  out[offset++] = 0xe0 | (spsBuffers.length & 0x1f)
  spsBuffers.forEach((buf) => {
    out[offset++] = (buf.byteLength >> 8) & 0xff
    out[offset++] = buf.byteLength & 0xff
    out.set(buf, offset)
    offset += buf.byteLength
  })
  out[offset++] = ppsBuffers.length & 0xff
  ppsBuffers.forEach((buf) => {
    out[offset++] = (buf.byteLength >> 8) & 0xff
    out[offset++] = buf.byteLength & 0xff
    out.set(buf, offset)
    offset += buf.byteLength
  })

  if (debugEnabled()) console.info(`[MP4WC] ${tag} rebuilt avcC`, codecLabel, out.byteLength)
  return out.buffer
}

function extractCodecDescription(track) {
  try {
    if (!track) return null
    const tag = track.audio ? 'audio track' : 'video track'
    const direct = extractEntryDescription(track, track?.codec, `${tag} (track level)`)
    if (direct) return direct

    const entry = track?.mdia?.minf?.stbl?.stsd?.entries?.[0] || null
    if (!entry) return null
    return extractEntryDescription(entry, track?.codec, `${tag} (stsd entry)`)
  } catch (e) {
    console.warn('extractCodecDescription failed:', e)
    return null
  }
}

function timescaleToMicroseconds(ts, timescale) {
  return Math.round((ts / timescale) * 1e6)
}

// Phase 6.A.3: WebCodecs requires fully qualified codec strings for AAC
// (e.g. `mp4a.40.2`). Some MP4 muxers (CapCut / QuickTime / avfoundation)
// emit a bare `mp4a` — accept it through our gate (see _isSupportedAudioCodec)
// but normalize it here before AudioDecoder.configure().
//
// AudioObjectType (AOT) lives in the top 5 bits of the first byte of the
// AudioSpecificConfig (the AAC decoder description). Common values:
//   2  = AAC-LC      → mp4a.40.2
//   5  = HE-AAC v1   → mp4a.40.5
//   29 = HE-AAC v2   → mp4a.40.29
// 31 means "extension AOT" follows (rare); for that and unknowns we fall back
// to AAC-LC since it's by far the most common and the decoder may still
// handle the actual stream correctly.
function normalizeAudioCodec(codec, description) {
  if (!codec) return 'mp4a.40.2'
  if (/^mp4a\.[0-9a-f]+(\.[0-9]+)?$/i.test(codec)) return codec
  if (!/^mp4a$/i.test(codec)) return codec
  try {
    if (description && description.byteLength > 0) {
      const view = new Uint8Array(description)
      const aot = (view[0] >> 3) & 0x1f
      if (aot > 0 && aot < 31) return `mp4a.40.${aot}`
    }
  } catch (e) { /* noop */ }
  return 'mp4a.40.2'
}

function normalizeSamples(samples, timescale) {
  if (!Array.isArray(samples) || !samples.length) return samples
  const firstCTS = samples[0].cts ?? samples[0].dts ?? 0
  const firstDTS = samples[0].dts ?? samples[0].cts ?? firstCTS
  return samples.map((sample, idx) => ({
    ...sample,
    number: idx,
    absoluteTimestampUs: timescaleToMicroseconds(sample.cts ?? sample.dts ?? 0, timescale),
    normalizedCTS: (sample.cts ?? 0) - firstCTS,
    normalizedDTS: (sample.dts ?? 0) - firstDTS,
    normalizedTimestampUs: timescaleToMicroseconds((sample.cts ?? sample.dts ?? 0) - firstCTS, timescale),
  }))
}

function hasKeyFrame(samples) {
  return Array.isArray(samples) && samples.some((s) => s.is_sync)
}

export class MP4WCPlayer {
  /**
   * @param {{
   *  onAudioDecoded?: (audioData: AudioData) => void,
   *  onVideoDecoded?: (videoFrame: VideoFrame) => void,
   *  preferHardware?: boolean,
   *  decodeAudio?: boolean,
   *  decodeVideo?: boolean,
   *  onAudioDecodeError?: (error: Error) => void,
   *  onPathLog?: (key: string, value: any) => void,  // Phase 6.A diagnostic
   * }} opts
   */
  constructor(opts = {}) {
    this.onAudioDecoded = opts.onAudioDecoded || (() => {})
    this.onVideoDecoded = opts.onVideoDecoded || (() => {})
    this.preferHardware = opts.preferHardware ?? true
    this.decodeAudio = opts.decodeAudio ?? true
    this.decodeVideo = opts.decodeVideo ?? true
    this.onAudioDecodeError = typeof opts.onAudioDecodeError === 'function' ? opts.onAudioDecodeError : () => {}
    // Phase 6.A: per-strip handlers can attach a path logger to capture decoder
    // decision points. Multiple strips may share an MP4WCPlayer (via the asset
    // decode manager) — the handler that most recently set this is the one
    // currently attributing events. Good enough for diagnostics.
    this.onPathLog = typeof opts.onPathLog === 'function' ? opts.onPathLog : null
    this._pathLog = (key, value) => {
      try { this.onPathLog?.(key, value) } catch (e) { /* noop */ }
    }

    this._mp4 = null
    this._info = null
    this._audioTrack = null
    this._videoTrack = null
    this._audioDecoder = null
    this._videoDecoder = null
    this._started = false
    this._videoNeedsKey = true
    this._videoSkipDebugCount = 0
    this._stats = {
      startedAt: 0,
      stoppedAt: 0,
      videoFramesDecoded: 0,
      audioFramesDecoded: 0,
      underruns: 0,
    }
    this._videoBaseTimestampUs = null
    this._audioConfigCheck = null
    this._loggedAudioConfig = false
    this._loggedAudioSupport = false
    this._loggedAudioConfigFailure = false
    this._audioConfigLog = null
    this._audioSupportLog = null
    this._pendingAudioChunks = []
    this._audioErrored = false
    this._audioCodecSupported = true
    this._pendingSeekSeconds = null
    this._pendingSeekTimestamp = null
    this._debugState = {
      id: MP4WCPlayer._nextId(),
      videoFramesDecoded: 0,
      audioFramesDecoded: 0,
      lastVideoPTS: null,
      lastAudioPTS: null,
      lastSeek: null,
      lastReset: null,
      lastKeyframePTS: null,
      decodeQueueSize: null,
      baseTimestampUs: null,
      needsKey: true,
      started: false,
      pendingSeek: null,
      lastSeekReplay: null,
    }

    this._onSamples = this._onSamples.bind(this)
    if (typeof window !== 'undefined') {
      if (!Array.isArray(window.__vybyMP4Players)) window.__vybyMP4Players = []
      window.__vybyMP4Players.push(() => this.getDebugState())
    }
  }

  get _hasMP4Box() {
    return (typeof window !== 'undefined' && !!window.MP4Box) || (typeof self !== 'undefined' && !!self.MP4Box)
  }

  async loadFromFile(file) {
    if (!this._hasMP4Box) {
      console.warn('MP4Box.js not found; MP4WCPlayer is inert')
      return
    }
    const buf = await file.arrayBuffer()
    await this._initMP4()
    this._appendBuffer(buf, 0)
    this._flush()
  }

  async loadFromBlob(blob) {
    if (!this._hasMP4Box) {
      console.warn('MP4Box.js not found; MP4WCPlayer is inert')
      return
    }
    if (this._loadedBlob === blob) return
    this._loadedBlob = blob
    const blobSize = blob?.size || 0
    const buf = await blob.arrayBuffer()
    if (debugEnabled()) console.debug('[MP4WC] loadFromBlob', { blobSize, bufferBytes: buf.byteLength })
    await this._initMP4()
    this._appendBuffer(buf, 0)
    this._flush()
  }

  async _initMP4() {
    if (this._mp4) return
    const MP4Box = (typeof window !== 'undefined' && window.MP4Box) ? window.MP4Box : ((typeof self !== 'undefined' && self.MP4Box) ? self.MP4Box : null)
    if (!MP4Box) throw new Error('MP4Box.js not present')
    this._mp4 = MP4Box.createFile()

    this._mp4.onError = e => console.error('MP4Box error:', e)
    this._mp4.onReady = async (info) => {
      this._info = info
      this._audioTrack = info.tracks.find(t => t.audio)
      this._videoTrack = info.tracks.find(t => t.video)
      if (debugEnabled()) {
        console.debug('[MP4WC] onReady', {
          tracks: info.tracks.map(t => ({ id: t.id, type: t.audio ? 'audio' : (t.video ? 'video' : 'other'), codec: t.codec })),
          movieTimescale: info.timescale,
        })
      }
      this._pathLog('mp4Ready', {
        audioTrack: !!this._audioTrack,
        videoTrack: !!this._videoTrack,
        audioCodec: this._audioTrack?.codec || null,
        sampleRate: this._audioTrack?.audio?.sample_rate || null,
        channels: this._audioTrack?.audio?.channel_count || null,
        allTrackCodecs: info.tracks.map(t => ({
          id: t.id,
          type: t.audio ? 'audio' : (t.video ? 'video' : 'other'),
          codec: t.codec,
        })),
        decodeAudio: this.decodeAudio,
      })
      if (this._audioTrack && this.decodeAudio) {
        if (!this._isSupportedAudioCodec(this._audioTrack.codec)) {
          this._audioCodecSupported = false
          this._pathLog('codecRejected', {
            codec: this._audioTrack.codec || null,
            stage: 'onReady',
          })
          this._notifyAudioError(new Error(`Unsupported audio codec: ${this._audioTrack.codec || 'unknown'}`))
        } else {
          this._enableExtraction(this._audioTrack.id)
        }
      }
      if (this._videoTrack && this.decodeVideo) this._enableExtraction(this._videoTrack.id)
      try {
        await this._applyPendingSeek()
      } catch (err) {
        console.warn('[MP4WC] pending seek replay failed', err)
      }
    }
    this._mp4.onSamples = this._onSamples
  }

  _appendBuffer(buf, fileStart) {
    const ab = buf instanceof ArrayBuffer ? buf : buf.buffer
    const b = ab.slice(0)
    b.fileStart = fileStart || 0
    this._mp4.appendBuffer(b)
  }

  _flush() {
    try { this._mp4.flush() } catch (e) { console.warn('flush error', e) }
  }

  _enableExtraction(trackId) {
    this._mp4.setExtractionOptions(trackId, null, { rapAlign: true, nbSamples: 0 })
  }

  startExtraction() {
    if (!this._mp4 || this._started) return
    try {
      this._mp4.start()
      this._started = true
      this._stats.startedAt = performance.now()
      this._debugState.started = true
    } catch (e) { /* noop */ }
  }

  stopExtraction() {
    if (!this._mp4 || !this._started) return
    try { this._mp4.stop() } catch (e) { /* noop */ }
    this._started = false
    this._stats.stoppedAt = performance.now()
    this._debugState.started = false
  }

  async flushDecoders() {
    const p = []
    if (this._audioDecoder) p.push(this._audioDecoder.flush().catch(() => {}))
    if (this._videoDecoder) p.push(this._videoDecoder.flush().catch(() => {}))
    await Promise.all(p)
    this._videoNeedsKey = true
    this._videoBaseTimestampUs = null
    this._debugState.needsKey = true
    this._debugState.baseTimestampUs = null
  }

  async resetDecoders() {
    if (this._seekLock && this._seekLockReset) {
      try {
        await this._seekLockReset
      } catch (e) { /* noop */ }
    }
    let release
    this._seekLockReset = new Promise((resolve) => { release = resolve })
    try { this._audioDecoder?.close() } catch (e) { /* noop */ }
    try { this._videoDecoder?.close() } catch (e) { /* noop */ }
    this._audioDecoder = null
    this._videoDecoder = null
    this._videoNeedsKey = true
    this._stats.audioFramesDecoded = 0
    this._stats.videoFramesDecoded = 0
    this._videoBaseTimestampUs = null
    this._debugState.audioFramesDecoded = 0
    this._debugState.videoFramesDecoded = 0
    this._debugState.needsKey = true
    this._debugState.baseTimestampUs = null
    this._debugState.lastReset = performance.now?.() || Date.now()
    if (release) release()
    this._seekLockReset = null
  }

  async seek(seconds) {
    const target = Number(seconds)
    if (!Number.isFinite(target) || target < 0) return
    if (!this._mp4 || !this._info) {
      this._pendingSeekSeconds = target
      this._pendingSeekTimestamp = performance.now?.() || Date.now()
      if (typeof window !== 'undefined' && window.vybyTimingDebug) {
        this._debugState.pendingSeek = {
          seconds: target,
          requestedAt: this._pendingSeekTimestamp,
          resolved: false,
        }
      }
      return
    }
    this._pendingSeekSeconds = null
    this._pendingSeekTimestamp = null
    if (typeof window !== 'undefined' && window.vybyTimingDebug) {
      this._debugState.pendingSeek = null
    }
    await this._doSeek(target)
  }

  async _doSeek(seconds) {
    if (!this._mp4 || !this._info) return
    if (this._seekLock) {
      try {
        await this._seekLock
      } catch (e) { /* noop */ }
    }
    let release
    this._seekLock = new Promise((resolve) => { release = resolve })
    if (typeof window !== 'undefined' && window.vybyTimingDebug) {
      this._debugState.lastSeek = {
        seconds,
        timestamp: performance.now?.() || Date.now(),
      }
    }
    try {
      await this.flushDecoders()
      try { this._mp4.stop() } catch (e) { /* noop */ }
      const movieScale = this._info.timescale || 1
      const movieTime = Math.max(0, seconds * movieScale)
      this._mp4.seek(movieTime, true)
      if (this._audioTrack && this.decodeAudio) this._enableExtraction(this._audioTrack.id)
      if (this._videoTrack && this.decodeVideo) this._enableExtraction(this._videoTrack.id)
      await this.resetDecoders()
      try { this._mp4.start() } catch (e) { /* noop */ }
      this._started = true
      this._stats.startedAt = performance.now()
    } finally {
      if (release) release()
      this._seekLock = null
    }
  }

  async _applyPendingSeek() {
    if (!this._mp4 || !this._info) return false
    if (!Number.isFinite(this._pendingSeekSeconds)) return false
    const target = this._pendingSeekSeconds
    const requestedAt = this._pendingSeekTimestamp
    this._pendingSeekSeconds = null
    this._pendingSeekTimestamp = null
    try {
      await this._doSeek(target)
      if (typeof window !== 'undefined' && window.vybyTimingDebug) {
        const now = performance.now?.() || Date.now()
        const replayInfo = {
          seconds: target,
          requestedAt,
          replayedAt: now,
        }
        this._debugState.pendingSeek = {
          ...replayInfo,
          resolved: true,
        }
        this._debugState.lastSeekReplay = replayInfo
      }
      return true
    } catch (err) {
      this._pendingSeekSeconds = target
      this._pendingSeekTimestamp = requestedAt
      throw err
    }
  }

  close() {
    try { this._mp4?.stop() } catch (e) { /* noop */ }
    try { this._audioDecoder?.close() } catch (e) { /* noop */ }
    try { this._videoDecoder?.close() } catch (e) { /* noop */ }
  }

  _ensureAudioDecoderConfig(sample) {
    if (!this._audioTrack || !this._audioTrack.audio) return
    if (this._audioDecoder && this._audioDecoder.state !== 'unconfigured') return

    const t = this._audioTrack
    const codec = t.codec
    if (!this._isSupportedAudioCodec(codec)) {
      this._audioCodecSupported = false
      this._audioSupportLog = {
        supported: false,
        codec,
        reason: 'unsupported-codec',
        timestamp: performance.now?.(),
      }
      this._pathLog('codecRejected', { codec: codec || null, stage: 'configure' })
      this._notifyAudioError(new Error(`Unsupported audio codec: ${codec || 'unknown'}`))
      return
    }
    const samplerate = t.audio?.sample_rate || 48000
    const channels = t.audio?.channel_count || 2
    let description = extractCodecDescription(t)
    if (!description && sample?.description) {
      description = extractEntryDescription(sample.description, codec, 'audio sample description')
    }
    // Phase 6.A.3: WebCodecs requires a fully qualified AAC codec string
    // (e.g. mp4a.40.2). Some MP4 muxers ship a bare "mp4a" — fill in the
    // profile suffix from AudioSpecificConfig (or default to AAC-LC) so
    // AudioDecoder.configure() doesn't reject the file.
    const decoderCodec = normalizeAudioCodec(codec, description)
    if (decoderCodec !== codec) {
      this._pathLog('codecNormalized', { from: codec, to: decoderCodec })
    }
    if (debugEnabled()) {
      console.info('[MP4WC] audio decoder config', {
        codec,
        decoderCodec,
        samplerate,
        channels,
        hasDescription: !!description,
        byteLength: description?.byteLength,
      })
    }

    if (typeof window === 'undefined' || !window.AudioDecoder) return
    this._audioDecoder = new window.AudioDecoder({
      output: (d) => {
        this._stats.audioFramesDecoded += d.numberOfFrames || 0
        if (debugEnabled()) {
          console.info('[MP4WC] audio output', {
            frames: d.numberOfFrames,
            sampleRate: d.sampleRate,
            format: d.format,
            duration: d.duration,
          })
        }
        this.onAudioDecoded(d)
        try {
          if (typeof window !== 'undefined' && window.vybyTimingDebug) {
            window.__vybyLastAudioFrame = {
              numberOfFrames: d?.numberOfFrames,
              duration: d?.duration,
              sampleRate: d?.sampleRate,
              format: d?.format,
              timestamp: performance.now?.() || Date.now(),
              ctxRate: window.__vybyAudioSampleRate || null,
            }
          }
        } catch (eLog) { /* noop */ }
        if (this._audioConfigLog) {
          this._audioConfigLog.lastOutput = {
            frames: d.numberOfFrames,
            sampleRate: d.sampleRate,
            format: d.format,
            time: performance.now?.() || Date.now(),
          }
        }
      },
      error: (e) => {
        console.error('AudioDecoder error', e)
        this._notifyAudioError(e)
      }
    })

    const config = {
      codec: decoderCodec,
      numberOfChannels: channels,
      sampleRate: samplerate,
    }
    if (description) config.description = description
    const descHead = (() => {
      try {
        if (!description) return null
        const view = new Uint8Array(description)
        const head = Array.from(view.slice(0, 16)).map((b) => b.toString(16).padStart(2, '0'))
        return head.join(' ')
      } catch (e) {
        return null
      }
    })()
    const baseLog = {
      codec: decoderCodec,
      originalCodec: codec,
      sampleRate: samplerate,
      channels,
      descriptionBytes: description ? description.byteLength : 0,
      descriptionHead: descHead,
    }
    this._audioConfigLog = { ...baseLog, timestamp: performance.now?.() }
    const configureDecoder = () => {
      if (debugEnabled() && !this._loggedAudioConfig) {
        this._loggedAudioConfig = true
        console.info('[MP4WC] audio configure()', baseLog)
      }
      try {
        this._audioDecoder.configure(config)
        this._pathLog('configured', { ...baseLog, withDescription: !!config.description })
        this._flushPendingAudioChunks()
      } catch (e) {
        this._pathLog('configureFailed', {
          ...baseLog,
          withDescription: !!config.description,
          error: e?.message || String(e),
        })
        if (debugEnabled() && !this._loggedAudioConfigFailure) {
          this._loggedAudioConfigFailure = true
          console.warn('[MP4WC] audio configure failed (with description)', { error: e?.message, ...baseLog })
        } else {
          console.warn('AudioDecoder.configure failed; retry without description', e)
        }
        delete config.description
        try {
          this._audioDecoder.configure(config)
          this._pathLog('configured', { ...baseLog, withDescription: false, retry: true })
          this._flushPendingAudioChunks()
        } catch (err) {
          this._pathLog('configureFailed', {
            ...baseLog,
            withDescription: false,
            retry: true,
            error: err?.message || String(err),
          })
          console.error('AudioDecoder.configure retry failed', err)
          this._notifyAudioError(err)
        }
      }
    }

    if (window.AudioDecoder && typeof window.AudioDecoder.isConfigSupported === 'function') {
      if (!this._audioConfigCheck) {
        const supportConfig = { ...config }
        this._audioConfigCheck = window.AudioDecoder.isConfigSupported(supportConfig)
          .then((result) => {
            this._audioSupportLog = {
              supported: !!result?.supported,
              codec: result?.config?.codec,
              numberOfChannels: result?.config?.numberOfChannels,
              sampleRate: result?.config?.sampleRate,
              descriptionBytes: result?.config?.description ? result.config.description.byteLength : 0,
              timestamp: performance.now?.()
            }
            this._pathLog('configSupport', {
              supported: !!result?.supported,
              codec: result?.config?.codec || baseLog.codec,
              numberOfChannels: result?.config?.numberOfChannels || baseLog.channels,
              sampleRate: result?.config?.sampleRate || baseLog.sampleRate,
              descriptionBytes: result?.config?.description ? result.config.description.byteLength : 0,
            })
            if (debugEnabled() && !this._loggedAudioSupport) {
              this._loggedAudioSupport = true
              console.info('[MP4WC] audio isConfigSupported', {
                supported: result?.supported,
                config: {
                  codec: result?.config?.codec,
                  numberOfChannels: result?.config?.numberOfChannels,
                  sampleRate: result?.config?.sampleRate,
                  descriptionBytes: result?.config?.description ? result.config.description.byteLength : 0,
                }
              })
            }
            if (result && result.supported && result.config) {
              Object.assign(config, result.config)
            }
            configureDecoder()
          })
          .catch((err) => {
            this._audioSupportLog = {
              supported: false,
              error: err?.message,
              timestamp: performance.now?.(),
              ...baseLog,
            }
            this._pathLog('configSupport', {
              supported: false,
              error: err?.message || String(err),
              ...baseLog,
            })
            if (debugEnabled()) {
              console.warn('[MP4WC] audio isConfigSupported failed', { error: err?.message, ...baseLog })
            }
            configureDecoder()
            this._notifyAudioError(err)
            this._flushPendingAudioChunks()
          })
          .finally(() => { this._audioConfigCheck = null })
      }
      return
    }

    configureDecoder()
  }

  _ensureVideoDecoderConfig(sample) {
    if (!this._videoTrack || !this._videoTrack.video) return
    if (this._videoDecoder && this._videoDecoder.state !== 'unconfigured') return

    const t = this._videoTrack
    const codec = t.codec
    let description = extractCodecDescription(t)
    if (!description && sample?.description) {
      description = extractEntryDescription(sample.description, codec, 'video sample description')
    }
    if (debugEnabled()) {
      console.debug('[MP4WC] video decoder config', {
        codec,
        hasDescription: !!description,
        byteLength: description?.byteLength,
      })
    }

    if (typeof window === 'undefined' || !window.VideoDecoder) return
    this._videoDecoder = new window.VideoDecoder({
      output: (vf) => {
        this._stats.videoFramesDecoded += 1
        this.onVideoDecoded(vf)
      },
      error: e => console.error('VideoDecoder error', e),
    })

    const config = {
      codec,
      codedWidth: t.video?.width || 0,
      codedHeight: t.video?.height || 0,
      hardwareAcceleration: this.preferHardware ? 'prefer-hardware' : 'no-preference',
      optimizeForLatency: true,
      colorSpace: undefined,
    }
    if (description) config.description = description
    try {
      this._videoDecoder.configure(config)
    } catch (e) {
      console.warn('VideoDecoder.configure failed; retry without description', e)
      delete config.description
      this._videoDecoder.configure(config)
    }
  }

  _onSamples(id, user, samples) {
    const isAudio = this._audioTrack && (id === this._audioTrack.id)
    const isVideo = this._videoTrack && (id === this._videoTrack.id)
    const track = isAudio ? this._audioTrack : (isVideo ? this._videoTrack : null)
    if (!track) return

    const timescale = track.timescale || 1
    const normalized = normalizeSamples(samples, timescale)
    if (isVideo && !hasKeyFrame(normalized)) {
      if (debugEnabled()) console.warn('[MP4WC] video sample batch without key frame; skipping', { count: normalized.length })
      return
    }
    for (const s of normalized) {
      if (isAudio) {
        // Phase 6.A.3: bail before _ensureAudioDecoderConfig when the codec
        // has already been rejected. The previous order re-ran the codec
        // check (and re-emitted codecRejected) for every AAC frame, producing
        // a tight loop on unsupported files.
        if (!this._audioCodecSupported) continue
        this._ensureAudioDecoderConfig(s)
        if (!this._audioDecoder) continue
      }
      if (isVideo) this._ensureVideoDecoderConfig(s)
      if (isVideo && (!this._videoDecoder || this._videoDecoder.state !== 'configured')) continue

      let ts = isVideo
        ? (s.absoluteTimestampUs ?? timescaleToMicroseconds(s.cts ?? s.dts ?? 0, timescale))
        : (s.normalizedTimestampUs ?? timescaleToMicroseconds(s.cts ?? 0, timescale))
      const dur = s.duration ? timescaleToMicroseconds(s.duration, timescale) : 0
      if (isVideo) {
        if (this._videoBaseTimestampUs == null) {
          this._videoBaseTimestampUs = ts
          this._debugState.baseTimestampUs = ts
          if (debugEnabled()) console.info('[MP4WC] video base timestamp set', { baseUs: ts })
        }
        const base = this._videoBaseTimestampUs || 0
        const relativeTs = ts - base
        this._emitOverlay?.({ type: 'decode-ts', raw: ts, norm: relativeTs })
      }
      const chunkDur = isVideo ? dur : dur
      const key = !!s.is_sync
      const data = new Uint8Array(s.data)

      try {
        if (isAudio && this._audioDecoder && typeof window !== 'undefined' && window.EncodedAudioChunk) {
          const chunk = new window.EncodedAudioChunk({ type: 'key', timestamp: ts, duration: dur, data })
          if (this._audioDecoder.state === 'configured') {
            if (debugEnabled()) {
              console.info('[MP4WC] decode audio chunk', {
                ts,
                dur,
                bytes: data?.byteLength || 0,
                pending: this._pendingAudioChunks.length,
                sourceRate: this._audioTrack?.audio?.sample_rate || null,
                ctxRate: (typeof window !== 'undefined' && window.__vybyAudioSampleRate) || null,
              })
            }
            if (typeof window !== 'undefined' && window.vybyTimingDebug) {
              this._debugState.lastAudioPTS = ts / 1e6
            }
            if (this._audioConfigLog) {
              this._audioConfigLog.lastChunk = {
                timestamp: ts,
                duration: dur,
                type: 'audio',
                length: data?.byteLength || 0,
                number: s.number ?? null,
                time: performance.now?.() || Date.now(),
              }
            }
            this._audioDecoder.decode(chunk)
          } else {
            this._pendingAudioChunks.push(chunk)
            if (debugEnabled()) {
              console.warn('[MP4WC] audio chunk queued before decoder ready', {
                ts,
                dur,
                bytes: data?.byteLength || 0,
                state: this._audioDecoder ? this._audioDecoder.state : 'missing',
                pending: this._pendingAudioChunks.length,
              })
            }
          }
        } else if (isVideo && this._videoDecoder && typeof window !== 'undefined' && window.EncodedVideoChunk) {
          if (this._videoNeedsKey && !key) {
            if (debugEnabled() && this._videoSkipDebugCount < 8) {
              console.debug('[MP4WC] skipping delta frame before key', { ts, dur, sampleIndex: s.number ?? null })
              this._videoSkipDebugCount += 1
              if (this._videoSkipDebugCount === 8) console.debug('[MP4WC] further delta skips muted')
            }
            continue
          }
          const chunk = new window.EncodedVideoChunk({ type: key ? 'key' : 'delta', timestamp: ts, duration: chunkDur, data })
          if (debugEnabled()) console.info('[MP4WC] decode chunk', { ts, duration: chunkDur, type: key ? 'key' : 'delta' })
          this._videoDecoder.decode(chunk)
          if (this._videoNeedsKey && key) {
            if (debugEnabled()) console.debug('[MP4WC] first key frame decoded', { ts, dur, sampleIndex: s.number ?? null })
            this._videoNeedsKey = false
            this._debugState.needsKey = false
            if (typeof window !== 'undefined' && window.vybyTimingDebug) {
              this._debugState.lastKeyframePTS = ts / 1e6
            }
            this._emitOverlay?.({ type: 'first-key', timestamp: ts })
          }
          if (typeof window !== 'undefined' && window.vybyTimingDebug) {
            this._debugState.lastVideoPTS = ts / 1e6
          }
        }
      } catch (e) {
        console.error('decode error', e)
        this._stats.underruns += 1
      }
    }

    if (isVideo) {
      const qSize = this._videoDecoder?.decodeQueueSize ?? null
      this._debugState.decodeQueueSize = qSize
      this._debugState.videoFramesDecoded = this._stats.videoFramesDecoded
      this._debugState.audioFramesDecoded = this._stats.audioFramesDecoded
      if (debugEnabled()) {
        const firstPTS = isVideo
          ? normalized[0]?.absoluteTimestampUs
          : normalized[0]?.normalizedTimestampUs
        const lastPTS = isVideo
          ? normalized[normalized.length - 1]?.absoluteTimestampUs
          : normalized[normalized.length - 1]?.normalizedTimestampUs
        console.info('[MP4WC] samples delivered', {
          count: normalized.length,
          firstPTS,
          lastPTS,
          baseUs: this._videoBaseTimestampUs,
          queueSize: qSize,
          needsKey: this._videoNeedsKey,
        })
      }
      this._emitOverlay?.({ type: 'stats', queue: qSize })
    }
  }

  _flushPendingAudioChunks() {
    if (!this._pendingAudioChunks.length || !this._audioDecoder || this._audioDecoder.state !== 'configured') return
    const chunks = this._pendingAudioChunks.splice(0)
    if (debugEnabled()) console.info('[MP4WC] flushing pending audio chunks', chunks.length)
    chunks.forEach((chunk) => {
      try {
        this._audioDecoder.decode(chunk)
      } catch (err) {
        console.error('[MP4WC] queued audio chunk decode failed', err)
        this._notifyAudioError(err)
      }
    })
  }

  _notifyAudioError(err) {
    if (this._audioErrored) return
    this._audioErrored = true
    this._pathLog('audioError', { message: err?.message || String(err) })
    try {
      this.onAudioDecodeError?.(err)
    } catch (handlerErr) {
      console.warn('onAudioDecodeError handler failed', handlerErr)
    }
  }

  clearAudioErrorFlag() {
    this._audioErrored = false
  }

  getStats() {
    return {
      startedAt: this._stats.startedAt,
      stoppedAt: this._stats.stoppedAt,
      audioFramesDecoded: this._stats.audioFramesDecoded,
      videoFramesDecoded: this._stats.videoFramesDecoded,
      underruns: this._stats.underruns,
      playing: this._started,
    }
  }

  setOverlayEmitter(fn) {
    this._emitOverlay = typeof fn === 'function' ? fn : null
  }

  getPlayerInfo() {
    return {
      video: this._videoTrack ? {
        id: this._videoTrack.id,
        codec: this._videoTrack.codec,
        timescale: this._videoTrack.timescale,
        width: this._videoTrack.video?.width,
        height: this._videoTrack.video?.height,
        samples: this._videoTrack.nb_samples,
      } : null,
      decoder: this._videoDecoder ? {
        state: this._videoDecoder.state,
        queueSize: this._videoDecoder.decodeQueueSize,
      } : null,
    }
  }

  getDebugState() {
    const state = {
      ...this._debugState,
      started: this._started,
      decodeQueueSize: this._videoDecoder?.decodeQueueSize ?? this._debugState.decodeQueueSize,
      videoFramesDecoded: this._stats.videoFramesDecoded,
      audioFramesDecoded: this._stats.audioFramesDecoded,
      needsKey: this._videoNeedsKey,
      baseTimestampUs: this._videoBaseTimestampUs,
    }
    if (!debugEnabled()) delete state.lastSeek
    return state
  }

  _isSupportedAudioCodec(codec) {
    if (!codec) return false
    // Accept common AAC codec strings: fully qualified (mp4a.40.2, mp4a.6B) or
    // bare 'mp4a' (CapCut/QuickTime/avfoundation muxers sometimes omit the
    // profile suffix). normalizeAudioCodec() will fill in a real codec string
    // before AudioDecoder.configure(), and isConfigSupported() is the real gate.
    return /^mp4a(\.|$)/i.test(codec)
  }

  static _nextId() {
    if (!this.__idCounter) this.__idCounter = 1
    const next = this.__idCounter
    this.__idCounter += 1
    return next
  }
}


